import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  CheckCircle2, Ban, XCircle, Eye, Search, Briefcase, Hourglass, MessageCircle, Copy, ExternalLink,
  KeyRound, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import HardDeleteDialog from "../../components/HardDeleteDialog";

const STATUS_TONE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
  rejected: "bg-slate-700 text-slate-300 border-slate-600",
};

export default function AdminDistributors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [welcomeBanner, setWelcomeBanner] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openDelete = (d) => setDeleteTarget(d);
  const onDeleted = () => { setDeleteTarget(null); load(); };

  const load = () => api.get("/admin/distributors").then((r) => setRows(r.data));
  const loadWd = () => api.get("/admin/withdrawals?status=pending").then((r) => setWithdrawals(r.data));

  useEffect(() => { load(); loadWd(); }, []);

  const act = async (id, action, label) => {
    try {
      const { data } = await api.post(`/admin/distributors/${id}/${action}`);
      toast.success(label);
      // If activating, show welcome wa.me link
      if (action === "activate" && data?.welcome_wa_link) {
        setWelcomeBanner({
          name: rows.find((r) => r.id === id)?.name || "Distributor",
          wa_link: data.welcome_wa_link,
          message: data.welcome_message,
        });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const viewDetail = async (id) => {
    const { data } = await api.get(`/admin/distributors/${id}`);
    setDetail(data);
  };

  const wdAct = async (wid, action, label) => {
    try {
      await api.post(`/admin/withdrawals/${wid}/${action}`);
      toast.success(label);
      loadWd();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const filtered = rows.filter((d) => {
    if (filter !== "all" && d.status !== filter) return false;
    if (q && !((d.name + " " + (d.email || "") + " " + (d.city || "") + " " + (d.mobile || "") + " " + (d.contact_name || "")).toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const submitReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/distributors/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null); setResetPwd("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  const counts = rows.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-distributors-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Distributors</h1>
        <p className="text-slate-400 mt-1">Onboard, approve, monitor your distributor network.</p>
      </div>

      {welcomeBanner && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 ring-1 ring-emerald-500/20 rounded-md p-4 flex items-start gap-3" data-testid="dist-welcome-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-200">
              {welcomeBanner.name} activated · send welcome WhatsApp
            </div>
            <div className="text-sm text-emerald-100/80 mt-1">
              Click below to open WhatsApp with a prefilled congratulations message, then hit Send.
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={welcomeBanner.wa_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-3.5 py-2"
                data-testid="dist-welcome-send"
              >
                <MessageCircle className="w-4 h-4" /> Send welcome on WhatsApp
                <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </a>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(welcomeBanner.message || ""); toast.success("Message copied"); }}
                className="inline-flex items-center gap-1.5 text-emerald-200 hover:text-emerald-100 text-xs font-semibold"
              >
                <Copy className="w-3 h-3" /> Copy message
              </button>
              <button
                type="button"
                onClick={() => setWelcomeBanner(null)}
                className="text-emerald-300 hover:text-emerald-100 text-xs ml-auto"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#151A23] border border-slate-800 rounded-md p-4">
          <div className="text-xs text-slate-500 uppercase font-semibold">Total</div>
          <div className="font-display text-2xl font-bold text-white tabular">{rows.length}</div>
        </div>
        <div className="bg-[#151A23] border border-slate-800 rounded-md p-4">
          <div className="text-xs text-emerald-400 uppercase font-semibold">Active</div>
          <div className="font-display text-2xl font-bold text-white tabular">{counts.active || 0}</div>
        </div>
        <div className="bg-[#151A23] border border-slate-800 rounded-md p-4">
          <div className="text-xs text-amber-400 uppercase font-semibold">Pending</div>
          <div className="font-display text-2xl font-bold text-white tabular">{counts.pending || 0}</div>
        </div>
        <div className="bg-[#151A23] border border-slate-800 rounded-md p-4">
          <div className="text-xs text-red-400 uppercase font-semibold">Suspended</div>
          <div className="font-display text-2xl font-bold text-white tabular">{counts.suspended || 0}</div>
        </div>
      </div>

      {withdrawals.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4" data-testid="admin-pending-withdrawals">
          <div className="flex items-center gap-2 text-amber-300 font-semibold mb-3">
            <Hourglass className="w-4 h-4" /> {withdrawals.length} pending withdrawal{withdrawals.length > 1 ? "s" : ""}
          </div>
          <div className="space-y-2">
            {withdrawals.map((w) => (
              <div key={w.id} className="flex items-center justify-between bg-slate-900/40 rounded p-3 text-sm" data-testid={`admin-wd-row-${w.id}`}>
                <div>
                  <div className="font-medium text-white">{w.distributor_name} · <span className="text-slate-400">{w.distributor_email}</span></div>
                  <div className="text-xs text-slate-500">{w.note || "No note"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-white tabular">{fmtPKR(w.amount)}</div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => wdAct(w.id, "approve", "Approved")} data-testid={`admin-wd-approve-${w.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => wdAct(w.id, "reject", "Rejected")} data-testid={`admin-wd-reject-${w.id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by name, email, mobile, city"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-500"
              data-testid="admin-dist-search"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {["all", "pending", "active", "suspended", "rejected"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`admin-dist-filter-${f}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border capitalize ${filter === f ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Distributor</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Joined</th>
                <th className="text-right px-4 py-3 font-semibold">Shops (Active)</th>
                <th className="text-right px-4 py-3 font-semibold">Revenue</th>
                <th className="text-right px-4 py-3 font-semibold">Earned</th>
                <th className="text-right px-4 py-3 font-semibold">Pending</th>
                <th className="text-right px-4 py-3 font-semibold">Wallet</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-500">No distributors found.</td></tr>
              )}
              {filtered.map((d) => (
                <tr key={d.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`admin-dist-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white flex items-center gap-2">
                      <Briefcase className="w-3.5 h-3.5 text-sky-400" />{d.name}
                    </div>
                    <div className="text-xs text-slate-500 tabular">{d.contact_name || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 tabular text-xs">{d.mobile || "—"}</td>
                  <td className="px-4 py-3 text-slate-400 tabular text-xs">{d.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{d.city || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${STATUS_TONE[d.status] || STATUS_TONE.pending}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 tabular text-xs">{(d.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-right tabular text-white">{d.shops_active}/{d.shops_total}</td>
                  <td className="px-4 py-3 text-right tabular text-white">{fmtPKR(d.revenue_generated)}</td>
                  <td className="px-4 py-3 text-right tabular text-emerald-300">{fmtPKR(d.commission_earned)}</td>
                  <td className="px-4 py-3 text-right tabular text-amber-300">{fmtPKR(d.commission_pending)}</td>
                  <td className="px-4 py-3 text-right tabular text-white">{fmtPKR(d.wallet_balance || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => viewDetail(d.id)} className="text-slate-300 hover:text-white" data-testid={`admin-dist-view-${d.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setResetTarget(d)} className="text-sky-300 hover:text-sky-200" title="Reset password" data-testid={`admin-dist-reset-${d.id}`}>
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      {d.status === "pending" && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(d.id, "activate", "Approved")} data-testid={`admin-dist-approve-${d.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => act(d.id, "reject", "Rejected")} data-testid={`admin-dist-reject-${d.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {d.status === "active" && (
                        <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => act(d.id, "suspend", "Suspended")} data-testid={`admin-dist-suspend-${d.id}`}>
                          <Ban className="w-4 h-4 mr-1" /> Suspend
                        </Button>
                      )}
                      {d.status === "suspended" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(d.id, "activate", "Re-activated")} data-testid={`admin-dist-activate-${d.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Activate
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openDelete(d)} className="text-red-400 hover:text-red-200" title="Delete permanently" data-testid={`admin-dist-delete-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-dist-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{detail?.distributor?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-xs text-slate-500 uppercase">Status</div><div className="font-display font-bold capitalize">{detail.distributor.status}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">City</div><div className="font-display font-bold">{detail.distributor.city}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Rate</div><div className="font-display font-bold tabular">{detail.distributor.commission_rate}%</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Wallet</div><div className="font-display font-bold tabular">{fmtPKR(detail.distributor.wallet_balance || 0)}</div></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Shops</div>
                  <div className="font-display text-xl font-bold tabular">{detail.shops_active}/{detail.shops_total}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Revenue</div>
                  <div className="font-display text-xl font-bold tabular">{fmtPKR(detail.revenue_generated)}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Earned</div>
                  <div className="font-display text-xl font-bold tabular text-emerald-300">{fmtPKR(detail.commission_earned)}</div>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="text-xs text-slate-400 uppercase font-semibold">Pending</div>
                  <div className="font-display text-xl font-bold tabular text-amber-300">{fmtPKR(detail.commission_pending)}</div>
                </div>
              </div>
              {detail.shops?.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-2">Shops onboarded</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detail.shops.map((s) => (
                      <div key={s.id} className="flex items-center justify-between bg-slate-800/40 rounded px-3 py-2 text-sm">
                        <span>{s.name} <span className="text-slate-500 text-xs">· {s.owner_email}</span></span>
                        <span className="text-xs text-slate-400 capitalize">{s.plan_id} · {s.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPwd(""); } }}>
        <DialogContent className="max-w-sm bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-dist-reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-sky-400" /> Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-3">
            <div className="text-sm text-slate-400">For <span className="font-bold text-white">{resetTarget?.name}</span> ({resetTarget?.email})</div>
            <Input
              type="password" required minLength={6}
              placeholder="New password (≥6 chars)"
              value={resetPwd} onChange={(e) => setResetPwd(e.target.value)}
              className="bg-slate-900 border-slate-700 text-slate-200"
              data-testid="admin-dist-reset-input"
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setResetTarget(null); setResetPwd(""); }} className="border-slate-700 text-slate-300">Cancel</Button>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="admin-dist-reset-submit">Reset</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hard delete confirmation */}
      <HardDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Referral Partner"
        targetLabel={deleteTarget?.name}
        endpoint={deleteTarget ? `/admin/distributors/${deleteTarget.id}` : ""}
        previewEndpoint={deleteTarget ? `/admin/distributors/${deleteTarget.id}/deletion-preview` : ""}
        description="Referral partner ka login deactivate hoga aur saare referred shops par attribution null ho jayega. Commission history audit ke liye preserved rahegi. Wallet balance > 0 ho to pehle settle karein."
        onDeleted={onDeleted}
      />
    </div>
  );
}
